import $ from 'jquery';
import Backbone from 'backbone';
import AutoResizeTextField from './AutoResizeTextField.js';
import AutoComplete from './AutoComplete.js';
import CalendarPopupView from './CalendarPopupView.js';

const KeyCodes = {
    /** @const */PAGE_UP: 33,
    /** @const */PAGE_DOWN: 34,
    /** @const */UP: 38,
    /** @const */DOWN: 40,
    /** @const */LEFT: 37,
    /** @const */RIGHT: 39,
    /** @const */ENTER: 13,
    /** @const */TAB: 9,
    /** @const */ESCAPE: 27,
    /** @const */SPACE: 32
};

/**
 * @expose
 * @enum {Filter.FilterType|number|undefined}
 * @const
 * @typedef {Filter.FilterType}
 */
const FilterType = {
    /** @expose @const*/ OPTION: 0,
    /** @expose @const*/ MULTIOPTION: 1,
    /** @expose @const*/ TEXT: 2,
    /** @expose @const*/ DATETIME: 3,
    /** @expose @const*/ DATE: 4,
    /** @expose @const*/ NUMBER: 5,
    /** @expose @const*/ BOOLEAN: 6
};

/**
 * @expose
 * @name Filter.Options
 * @property {Object<String, Filter.Config>} config
 * @property {String} type
 * @property {String} mode
 * @property {*} value
 * @property {*} valuePlaceholder
 * @property {*} value2
 * @property {Boolean} lockRemove=false
 * @property {Boolean} lockMode=false
 * @property {Boolean} readonly=false
 * @property {Boolean} allowMultiBoolean=false
 */

/**
 * @expose
 * @name Filter.Config
 * @property {String} label
 * @property {Filter.FilterType} type
 * @property {*} source
 * @property {Boolean} cacheSource=false
 * @property {Boolean} selectionEnabled=true
 * @property {Object[]|null} [supportedModes=null] - Limit supported modes (i.e for date fields)
 * @property {Object[]|null} [excludeModes=null] - Modes to exclude (i.e for date fields)
 * @property {boolean} sortItems=true
 */

/** @const */
const DECIMAL_SEPARATOR = (1.1).toLocaleString().substr(1, 1);
/** @const */
const DECIMAL_SEPARATOR_REGEX = new RegExp('\\' + DECIMAL_SEPARATOR, 'g');

const isValuelessMode = function (mode) {
    if (!mode || typeof mode !== 'string') return false;

    const comparison = mode.substr(-1);

    //noinspection PointlessBooleanExpressionJS
    return !!(comparison === 'H' ||
        comparison === 'h' ||
        comparison === 'd' ||
        comparison === 'w' ||
        comparison === 'm' ||
        comparison === 'y');
};

const Filter = Backbone.View.extend({

    tagName: 'div',
    className: 'filter',

    /**
     * @expose
     * @param {Filter.Options} options
     * */
    initialize: function (options) {

        /**
         * @type {Filter.Options}
         */
        this.options = options = Object.assign({}, options);

        if (!options.lockRemove) {
            this.$el
                .append(
                    $('<div class="filter-remove">')
                        .on('click', () => {
                            this.removeFilter();
                        })
                );
        }

        if (this.options.type != null) {
            this._initTypeField();
            this._initValueField();

            if (this._config.type === FilterType.NUMBER ||
                this._config.type === FilterType.DATETIME ||
                this._config.type === FilterType.DATE) {

                this._setValue({value: this.options.mode}, true);

                this._isValuelessMode = isValuelessMode(this.options.mode);

                if (!this._isValuelessMode) {
                    this._initValueFirstField();

                    if (this.options.mode === '<>') {
                        this._initValueSecondField();
                    }

                    if (this.options.value == null) {
                        this._inputFirst.$el.focus();
                    } else if (this.options.value2 == null && this.options.mode === '<>') {
                        this._inputSecond.$el.focus();
                    }
                }

            } else {
                if (this.options.value != null) {
                    this._setValue(this.options.value, true);
                } else {
                    this.focusValueInput();
                }
            }
        } else {
            this._initTypeField();
            this.focusValueInput();
        }
    },

    _initTypeField: function () {
        if (this._typeInput) return;
        if (this.options.type) {
            this._config = this.options.config[this.options.type];
            this.$el.append($('<div class="filter-label">').text(this._config.label + ':'));
        } else {
            const typeInput = this._typeInput = new AutoResizeTextField({className: 'filter-input'});
            this.$el.append(typeInput.render().$el);

            const items = [];
            for (let key in this.options.config) {
                if (!this.options.config.hasOwnProperty(key)) continue;
                if (this.options.config[key].selectionEnabled === false) continue;

                items.push({
                    value: key,
                    label: this.options.config[key].label
                });
            }

            items.sort((a, b) => a.label < b.label ? -1 : a.label > b.label ? 1 : 0);

            const autoComplete = this._autoComplete = new AutoComplete({
                className: 'filter-dropdown'
                , source: items
                , autoFocus: true
                , minLength: 0
                , delay: 100
                , target: typeInput.$el
                , toggleOnClick: 'empty'
                , autoSearchOnFocus: 'empty'
                , updateTargetValue: false // Do not update the input when focusing on an item
            });

            this.listenTo(typeInput, 'widthchange', () => {
                autoComplete.repositionAndResize();
                this.trigger('sizechange');
            });

            this
                .listenTo(autoComplete, 'select', (event) => {
                    const item = event.item;

                    this.options.type = item.value;

                    this.stopListening(autoComplete);
                    this.stopListening(typeInput);

                    autoComplete.remove();
                    typeInput.remove();

                    delete this._autoComplete;

                    this._typeInput = null;
                    this._initTypeField();
                    this._initValueField();
                    if (!window.event || window.event.keyCode === undefined || window.event.keyCode !== KeyCodes.TAB || !window.event.shiftKey) {
                        this.focusValueInput();
                    }
                    this.trigger('sizechange');
                    this.trigger('typeselect', item.value);
                })
                .listenTo(autoComplete, 'inputupdate', () => { typeInput.update(); }) // Auto-resize
                .listenTo(autoComplete, 'hide', () => { this.removeFilter(); });

            setTimeout(() => {
                if (this.$el) {
                    this.$el.addClass('editing');
                }
            });
        }

        return this;
    },

    uncacheSource: function () {
        if (this._config && (this._config.type === FilterType.OPTION || this._config.type === FilterType.MULTIOPTION) && this._input) {
            this._autoComplete.uncacheSource();
        }
        return this;
    },

    _initValueField: function () {
        const options = this.options;
        const config = this._config;

        let conditional = config.type === FilterType.NUMBER ||
            config.type === FilterType.DATETIME ||
            config.type === FilterType.DATE;

        const input = this._input = new AutoResizeTextField({className: 'filter-input'});
        input.$el.attr('placeholder', this.options.valuePlaceholder || '');
        this.$el.append(input.render().$el);

        if ((conditional && this.options.lockMode) || this.options.readonly) {
            input.$el.prop('readonly', true);
            return this;
        }

        let isMultiSelect = config.type === FilterType.MULTIOPTION || (
            config.type === FilterType.BOOLEAN && config.allowMultiBoolean
        );

        let source = null;
        if (config.type === FilterType.BOOLEAN) {
            source = [{
                value: true,
                label: options.i18n.t('yes')
            },
            {
                value: false,
                label: options.i18n.t('no')
            }];
        } else if (conditional) {

            source = options.i18n.t('filter_condition_order');
            if (config.type !== FilterType.DATETIME && config.type !== FilterType.DATE) {
                source = source.filter(value => !/[Hhdwmy]$/.test(value));
            }

            if (config.supportedModes) {
                source = source.filter(x => config.supportedModes.indexOf(x) !== -1);
            }

            if (config.excludeModes) {
                source = source.filter(x => config.excludeModes.indexOf(x) === -1);
            }

            source = source.map(value => ({
                value: value,
                label: options.i18n.t('filter_condition', value)
            }));

        } else if ((config.type === FilterType.OPTION || config.type === FilterType.MULTIOPTION) && config.source) {
            source = config.source;
        }
        if ((config.type === FilterType.OPTION || config.type === FilterType.MULTIOPTION) && !source) {
            source = []; // Still treat values like when autocomplete box
        }

        if (source) {
            const autoComplete = this._autoComplete = new AutoComplete({
                className: 'filter-dropdown'
                , source: source
                , cacheSource: config.cacheSource
                , autoFocus: true
                , minLength: 0
                , delay: 100
                , target: input.$el
                , multi: isMultiSelect
                , toggleOnClick: 'empty'
                , autoSearchOnFocus: 'empty'
                , updateTargetValue: false // Do not update the input when focusing on an item
                , sortItems: config.sortItems === undefined || !!config.sortItems
            });

            const value = this.options.value;

            if (isMultiSelect && value) {
                autoComplete.setCheckedItems(value);

                if (value[0] && value[0].value === Filter.SelectAllValue) {
                    this.listenToOnce(autoComplete, 'response', content => {
                        if (this.value().length === 1 && this.value()[0].value === Filter.SelectAllValue) {
                            autoComplete.setCheckedItems(content);
                        }
                    });
                }
            }

            let willFocusOnNextField = false;

            this.listenTo(input, 'widthchange', () => {
                autoComplete.repositionAndResize();
                this.trigger('sizechange');
            });

            input.$el
                .on('focus', () => {
                    willFocusOnNextField = false;
                    this.$el.addClass('editing');
                })
                .on('blur', () => {
                    if (this.options) { // If we are not closed
                        this.$el.removeClass('editing');
                        setTimeout(() => {

                            const value = this.options.value;

                            let focusIsInsideThisView = this.$el.has(document.activeElement).length > 0;

                            const isValueArray = this._config.type === Filter.FilterType.OPTION ||
                                this._config.type === Filter.FilterType.MULTIOPTION;

                            // Do we have no value selected/typed in?
                            let canRemove = (isValueArray && Array.isArray(value) && !value.length)
                                || (!isValueArray && value == null);

                            // Make sure we're not in a mode that is good with the value not being present
                            canRemove = canRemove && !this._isValuelessMode && !this.options.lockRemove;

                            // Make sure we are not in a transition to the next field
                            canRemove = canRemove && !willFocusOnNextField;

                            // Make sure we are not currently selecting the condition, and we're not in focus
                            canRemove = canRemove && (!conditional || !focusIsInsideThisView);

                            if (canRemove) {
                                this.removeFilter();
                            } else if (conditional && !focusIsInsideThisView) {
                                this._triggerLateValueEvent();
                            }
                        });
                    }
                });

            this
                .listenTo(autoComplete, 'select', (event) => {
                    const item = event.item;

                    this._isValuelessMode = conditional && isValuelessMode(item.value);

                    if (!isMultiSelect) {
                        this._setValue(item, conditional && !this._isValuelessMode);
                        if (conditional && !this._isValuelessMode) {
                            willFocusOnNextField = true;
                            setTimeout(() => {
                                this._initValueFirstField();
                                if (!window.event || window.event.keyCode !== KeyCodes.TAB || !window.event.shiftKey) {
                                    this._inputFirst.$el.focus();
                                }
                            });
                        } else {
                            if (this._isValuelessMode) {
                                this._removeValueFields();
                            } else {
                                setTimeout(() => {
                                    input.$el.blur();
                                });
                            }
                        }
                    }
                })
                .listenTo(autoComplete, 'inputupdate', () => { input.update(); }) // Auto-resize
                .listenTo(autoComplete, 'open', () => {
                    const value = conditional ? this.options.mode : this.options.value;
                    if (config.type !== FilterType.MULTIOPTION && value != null) {
                        autoComplete.setFocusedValue(value);
                    }
                })
                .listenTo(autoComplete, 'hide', () => {
                    const value = conditional ? this.options.mode : this.options.value;
                    if ((value == null && !this.options.lockRemove) && !this.$el.has(document.activeElement).length) {
                        this.removeFilter();
                    }
                })
                .listenTo(autoComplete, 'check', event => { // FilterType.MULTIOPTION only
                    const item = event.item;

                    let checkedItems = [];

                    if (item.value === Filter.SelectAllValue) {
                        if (item.checked) {
                            checkedItems.push({
                                'label': item['short_label'] != null ? item['short_label'] : item['label'],
                                'value': item['value'],
                            });
                            autoComplete.setCheckedItems(autoComplete.itemsReference());
                        } else {
                            autoComplete.setCheckedItems([]);
                        }
                    } else {
                        checkedItems = autoComplete.getCheckedItems();

                        if (checkedItems.length && checkedItems[0].value === Filter.SelectAllValue) {
                            checkedItems.shift();
                            autoComplete.setCheckedItems(checkedItems);
                        }

                        // Clean up items
                        checkedItems = checkedItems.map(item => ({
                            'label': item['short_label'] != null ? item['short_label'] : item['label'],
                            'value': item['value']
                        }));
                    }

                    // Set the value
                    this._setValue(checkedItems);
                });

        } else {
            input.$el
                .on('focus', () => {
                    this.$el.addClass('editing');
                })
                .on('blur', () => {
                    this.$el.removeClass('editing');

                    const value = input.$el.val();
                    if (value || this.options.lockRemove) {
                        this._setValue({value: value, label: value});
                    } else {
                        this.removeFilter();
                    }

                });
        }

        // TODO: on blur- fix display value from autocomplete inputs

        return this;
    },

    _removeValueFields: function () {
        return this._removeFirstValueField()._removeSecondValueField();
    },

    _removeFirstValueField: function () {
        if (this._inputFirst) {
            if (this.calendarpopup) {
                this.calendarpopup.unhookFromInput(this._inputFirst.$el);
            }
            if (this._inputFirstCalendarHook) {
                this.stopListening(this.calendarpopup, 'dateselect', this._inputFirstCalendarHook);
                this._inputFirstCalendarHook = null;
            }
            this._inputFirst.remove();
            this._inputFirst = null;
        }
        return this;
    },

    _removeSecondValueField: function () {
        if (this._inputSecond) {
            if (this.calendarpopup) {
                this.calendarpopup.unhookFromInput(this._inputSecond.$el);
            }
            if (this._inputSecondCalendarHook) {
                this.stopListening(this.calendarpopup, 'dateselect', this._inputSecondCalendarHook);
                this._inputSecondCalendarHook = null;
            }
            this._inputSecond.remove();
            this._inputSecond = null;
            this._secondLabel.remove();
            this._secondLabel = null;
        }
        return this;
    },

    _initValueFirstField: function () {
        if (this._inputFirst) {

            this._inputFirst.$el.val(this._normalizeValueForInput(this.options.value));

            if ((this.options.mode === '<>') !== !!this._inputSecond) {
                if (this.options.mode === '<>') {
                    this._initValueSecondField();
                } else {
                    if (this.calendarpopup) {
                        this.calendarpopup.unhookFromInput(this._inputSecond.$el);
                    }
                    this._removeSecondValueField();
                }
            }

        } else {

            const input = this._inputFirst = new AutoResizeTextField({className: 'filter-input'});
            this.$el.append(input.render().$el.val(this._normalizeValueForInput(this.options.value)));

            if (this.options.readonly) {
                input.$el.prop('readonly', true);
            }

            if (this._config.type === FilterType.NUMBER) {
                input.$el.numeric().css('direction', 'ltr');
            }

            input.$el
                .on('focus', () => {
                    this.$el.addClass('editing');
                })
                .on('blur', () => {
                    setTimeout(() => {
                        const value = this._normalizeValueFromInput(this._inputFirst.$el.val());
                        if (value || this.$el.has(document.activeElement).length || this.options.lockRemove) {
                            if (this.$el.has(document.activeElement).length) {
                                this._setValueFirst(value, true);
                            } else {
                                this._setValueFirst(value);
                                if (!this.$el.has(document.activeElement).length) {
                                    this.$el.removeClass('editing')
                                }
                                this._triggerLateValueEvent();
                            }
                            input.$el.val(this._normalizeValueForInput(value));
                            input.update();
                        } else {
                            this.removeFilter();
                        }
                    });
                });

            if (this._config.type === FilterType.DATETIME ||
                this._config.type === FilterType.DATE) {

                this.calendarpopup = new CalendarPopupView({ i18n: this.options.i18n });
                this.calendarpopup.hookUpToInput(input.$el, 'dd/MM/yyyy');
                this.listenTo(this.calendarpopup, 'dateselect', () => {
                    if (document.activeElement === this._inputFirst.el) {
                        setTimeout(() => {
                            if (this._inputSecond) {
                                this._inputSecond.$el.focus();
                            } else {
                                this._inputFirst.$el.blur();
                            }
                        });
                    }
                });
            }

            if (this.options.mode === '<>') {
                this._initValueSecondField();
            }
        }
        return this;
    },

    _initValueSecondField: function () {
        const options = this.options;

        if (this._inputSecond) {

            this._inputSecond.$el.val(this._normalizeValueForInput(options.value2));

        } else {

            this._secondLabel = $('<div class="filter-label">')
                .text(options.i18n.t('filter_condition_between_and'))
                .appendTo(this.$el);

            const input = this._inputSecond = new AutoResizeTextField({className: 'filter-input'});
            this.$el.append(input.render().$el.val(this._normalizeValueForInput(options.value2)));

            if (options.readonly) {
                input.$el.prop('readonly', true);
            }

            if (this._config.type === FilterType.NUMBER) {
                input.$el.numeric().css('direction', 'ltr');
            }

            if (this._config.type === FilterType.DATETIME ||
                this._config.type === FilterType.DATE) {

                this.calendarpopup.hookUpToInput(input.$el, 'dd/MM/yyyy');
                this.listenTo(this.calendarpopup, 'dateselect', function () {
                    if (document.activeElement === this._inputSecond.el) {
                        this._inputSecond.$el.blur();
                    }
                });
            }

            input.$el
                .on('focus', () => {
                    this.$el.addClass('editing');
                })
                .on('blur', () => {
                    setTimeout(() => {
                        const value = this._normalizeValueFromInput(this._inputSecond.$el.val());
                        if (value || this.$el.has(document.activeElement).length || this.options.lockRemove) {
                            if (this.$el.has(document.activeElement).length) {
                                this._setValueSecond(value, true);
                            } else {
                                this._setValueSecond(value);
                                if (!this.$el.has(document.activeElement).length) {
                                    this.$el.removeClass('editing')
                                }
                                this._triggerLateValueEvent();
                            }
                            input.$el.val(this._normalizeValueForInput(value));
                            input.update();
                        } else {
                            this.removeFilter();
                        }
                    });
                });
        }
        return this;
    },

    _normalizeValueForInput: function (value) {
        if (value === '' || value == null) return '';

        if (typeof value == 'number') {
            value = value.toString();
            if (DECIMAL_SEPARATOR !== '.') {
                value = value.replace(/\./g, DECIMAL_SEPARATOR);
            }
            return value;
        } else if (value instanceof Date) {
            return this.options.i18n.formatDate(value, 'dd/MM/yyyy');
        }

        return value.toLocaleString();
    },

    _normalizeValueFromInput: function (value) {
        if (value === '' || value == null) {
            if (this._config.type === FilterType.DATETIME ||
                this._config.type === FilterType.DATE) return null;
            return value;
        }

        if (typeof value != 'number' && this._config.type === FilterType.NUMBER) {
            return parseFloat(value.replace(DECIMAL_SEPARATOR_REGEX, '.'));
        } else if (!(value instanceof Date) &&
            (this._config.type === FilterType.DATETIME || this._config.type === FilterType.DATE)) {

            value = this.options.i18n.parseDate(value, 'dd/MM/yyyy');

            if (this.options.mode === '<=' || this.options.mode === '>') {
                value.setDate(value.getDate() + 1);
                value.setHours(0);
                value.setMilliseconds(-1);
            }

            return value;
        }

        return value;
    },

    _setValue: function (values, silent) {
        const options = this.options;

        if (this._config.type === FilterType.NUMBER ||
            this._config.type === FilterType.DATETIME ||
            this._config.type === FilterType.DATE) {
            if (silent && options._oldMode === undefined) {
                options._oldMode = options.mode
            }
            options.mode = values.value;
            this._input.value(options.i18n.t('filter_condition', options.mode));
            if (!silent) {
                if (options.mode === '<>') {
                    this.trigger('value', options.value, options.value2);
                } else if (this._isValuelessMode) {
                    this.trigger('value', options.mode);
                } else {
                    this.trigger('value', options.value);
                }
                options._oldValue = undefined;
                options._oldValue2 = undefined;
                options._oldMode = undefined;
            }
        } else {
            if (typeof values === 'number' || typeof values === 'string') { // When receiving plain text value (i.e an autocomplete keyword)
                values = {value: values, label: values};
            } else if (typeof values === 'boolean') {
                values = {value: values, label: options.i18n.t(values ? 'yes' : 'no')};
            }

            options.value = values;
            if ($.isArray(values)) {
                let label = '';
                for (let i = 0; i < values.length; i++) {
                    if (label) label += ', ';
                    label += values[i].label;
                }
                this._input.value(label);
            } else {
                this._input.value(typeof values.label === 'undefined' ? values : values.label);
                if (this._config.type !== Filter.FilterType.OPTION && this._config.type !== Filter.FilterType.MULTIOPTION) {
                    values = values.value;
                }
            }
            if (!silent) {
                this.trigger('value', values);
            }
        }
        return this;
    },

    _setValueFirst: function (value, silent) {
        const options = this.options;
        if (silent && options._oldValue === undefined) {
            options._oldValue = options.value
        }
        options.value = value;
        if (!silent) {
            if (options.mode === '<>') {
                this.trigger('value', options.value, options.value2);
            } else {
                this.trigger('value', options.value);
            }
            options._oldValue = undefined;
            options._oldValue2 = undefined;
            options._oldMode = undefined;
        }
        return this;
    },

    _setValueSecond: function (value, silent) {
        const options = this.options;
        if (silent && options._oldValue2 === undefined) {
            options._oldValue2 = options.value2
        }
        options.value2 = value;
        if (!silent) {
            this.trigger('value', options.value, options.value2);
            options._oldValue = undefined;
            options._oldValue2 = undefined;
            options._oldMode = undefined;
        }
        return this;
    },

    _triggerLateValueEvent: function () {
        const options = this.options;
        if ((options._oldValue !== undefined && options._oldValue !== options.value) ||
            (options._oldValue2 !== undefined && options._oldValue2 !== options.value2) ||
            (options._oldMode !== undefined && options._oldMode !== options.mode)) {
            if (options.mode === '<>') {
                this.trigger('value', options.value, options.value2);
            } else {
                this.trigger('value', options.value);
            }
        }
        options._oldValue = undefined;
        options._oldValue2 = undefined;
        options._oldMode = undefined;
        return this;
    },

    focusTypeInput: function () {
        if (this._typeInput) {
            this._typeInput.$el.focus();
        }
        return this;
    },

    focusValueInput: function () {
        if (this._input) {
            this._input.$el.focus();
        }
        return this;
    },

    render: function () {

        return this;
    },

    removeFilter: function () {
        this.trigger('remove');
        this.$el.remove();
        this.trigger('afterremove');
        this.remove();
        return this;
    },

    remove: function () {
        if (this._autoComplete) {
            this._autoComplete.remove();
        }
        if (this.calendarpopup) {
            this.calendarpopup.remove();
        }
        if (this._inputSecond) {
            this._inputSecond.remove();
        }
        if (this._inputFirst) {
            this._inputFirst.remove();
        }
        if (this._input) {
            this._input.remove();
        }
        if (this._typeInput) {
            this._typeInput.remove();
        }

        this.$el.find('.filter-remove').off();
        this.$el.find('.filter-label').off();

        Filter.__super__.remove.apply(this, arguments);

        // Clean up any references to anything...
        for (let key of Object.keys(this)) {
            this[key] = null;
        }
    },

    type: function () {
        return this.options.type;
    },

    value: function () {
        if (this._config.type === Filter.FilterType.TEXT ||
            (this._config.type === Filter.FilterType.BOOLEAN && !this._config.allowMultiBoolean)) {
            return this.options.value ? this.options.value.value : null;
        } else {
            return this.options.value;
        }
    },

    value2: function () {
        return this.options.value2;
    },

    mode: function () {
        return this.options.mode;
    }
});

Filter.FilterType = FilterType;

Filter.SelectAllValue = Symbol('select_all');

Filter.isValuelessMode = isValuelessMode;

export default Filter;
